import React, { useState } from "react";
import { FaFileUpload } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { getMaxFileSize } from "@/utils/helpers";
import { AppConfig } from "@/utils/AppConfig";
import i18n from "@/i18n";

interface FileInputExcelProps {
  onFileChange: (file: File | null) => void;
  file: File | null;
  onRemoveFile: () => void;
  disabled?: boolean;
}

const { MAX_FILE_SIZE, MAX_FILE_SIZE_MB_String } = getMaxFileSize(
  AppConfig.FileMaxSizeInMB.productsExcel,
);

const FileInputExcel: React.FC<FileInputExcelProps> = ({
  onFileChange,
  disabled,
  onRemoveFile,
  file,
}) => {
  const { t } = useTranslation();
  const [, setFile] = useState<File | null>(file || null);
  const [fileName, setFileName] = useState<string | null>(file?.name || null);
  const [error, setError] = useState<string | null>(null);

  const maxSize =
    i18n.language === "ar"
      ? MAX_FILE_SIZE_MB_String.ar
      : MAX_FILE_SIZE_MB_String.en;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      const fileType = selectedFile.type;

      if (
        fileType !== "application/vnd.ms-excel" &&
        fileType !==
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        setError(t("fileInputExcel.invalidType"));
        setFile(null);
        setFileName(null);
        onFileChange(null);
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE) {
        setError(t("fileInputExcel.fileTooLarge", { size: maxSize }));
        setFile(null);
        setFileName(null);
        onFileChange(null);
        return;
      }

      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError(null);
      onFileChange(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileName(null);
    setError(null);
    onFileChange(null);
    onRemoveFile();

    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <div className="flex w-full max-w-full flex-wrap items-center gap-1">
      <div className="flex w-full rounded-md border border-gray-300">
        <input
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleFileChange}
          id="file-input"
          name="excel-file"
          disabled={disabled}
        />

        <button
          className={`min-w-[80px] rounded-s-md px-3 py-1.5 text-sm font-semibold ${
            disabled
              ? "cursor-not-allowed bg-gray-300 text-gray-500"
              : "cursor-pointer bg-sidebar text-skyblue"
          }`}
          onClick={() => document.getElementById("file-input")?.click()}
        >
          {t("fileInputExcel.upload")}
        </button>

        <div className="relative flex w-full min-w-[120px] flex-col items-center justify-center px-2 py-2 text-xs sm:text-sm">
          {!fileName && !error && (
            <div
              className={`flex items-center justify-center gap-1 font-semibold ${
                disabled ? "text-gray-500" : "text-sidebar"
              }`}
            >
              <FaFileUpload size={16} />
              <span>{t("fileInputExcel.chooseLocation")}</span>
            </div>
          )}

          {error && (
            <div className="text-wrap p-1 px-2 text-xs text-red-500">
              {error}
            </div>
          )}

          {fileName && (
            <div className="flex w-full flex-col items-center px-1">
              <p className="max-w-[150px] truncate sm:max-w-[200px]">
                {fileName}
              </p>
            </div>
          )}

          {fileName && (
            <button
              className="absolute end-0 top-0 p-0.5 text-base text-red-500 hover:bg-black/10"
              onClick={handleRemoveFile}
            >
              <IoMdClose size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileInputExcel;
